import type { ConstraintDisplayMode } from '@/portfolio/proposal-builder/components/interactive-bar-chart/types'

import {
  type FilterObject,
  isAndCondition,
  isNotCondition,
  isOrCondition,
} from '@moment/trading-types/filter-expression'
import isNil from 'lodash/isNil'

import { PROPOSAL_FIELD_LABELS } from '@/components/molecules/proposal-builder/constants'
import { globalLogger } from '@/lib/logger'
import { getCurrencyCode } from '@/portfolio/proposal-builder/helpers/get-currency-symbol'
import { isDollarMode } from '@/portfolio/shared/display-mode/utils'
import {
  type AllocationConstraint,
  type AverageValueConstraint,
  type BuyUniverseConstraint,
  type CashflowBandConstraint,
  type ConstraintFilterCondition,
  type DiversificationConstraint,
  type FilterDictionaryFilter,
  type LadderPolicyConstraint,
  type ModelConstraint,
  ModelConstraintType,
  type Piece,
  type PiecewiseConstraint,
  type UniverseConstraint,
} from '@/types/api/constraints'
import { describeFilter as describeFilterTree } from '@/utils/live-inventory/describe-filter'
import { VERBOSE_STYLE } from '@/utils/live-inventory/describe-filter-condition'
import {
  capitalize,
  decimalToPercentage,
  formatMoney,
  formatPercentage,
} from '@/utils/presentation/formatting'
import {
  DEFAULT_TRANSFORMS,
  referenceDataTransformationConfig,
} from '@/utils/presentation/instrument-data-transforms'

import { readAccountValueBound } from './read-account-value-bound'
import {
  type AllocationBounds,
  ConstraintInstanceType,
  type DiversificationBounds,
} from './types'

type ConditionValue =
  | ConstraintFilterCondition[keyof ConstraintFilterCondition]
  | null
  | undefined

export const MAX_FILTER_DEPTH = 10

// Recursively search a filter tree for a specific field's condition
export function findFieldCondition(
  filter: FilterObject | null | undefined,
  field: string,
  depth = 0,
): ConstraintFilterCondition | null {
  if (!filter || depth > MAX_FILTER_DEPTH) return null

  if (isAndCondition(filter)) {
    for (const nestedFilter of filter.and) {
      const found = findFieldCondition(nestedFilter, field, depth + 1)
      if (found) return found
    }
  }

  if (isOrCondition(filter)) {
    for (const nestedFilter of filter.or) {
      const found = findFieldCondition(nestedFilter, field, depth + 1)
      if (found) return found
    }
  }

  if (isNotCondition(filter)) {
    const found = findFieldCondition(filter.not, field, depth + 1)
    if (found) return found
  }

  for (const [key, value] of Object.entries(filter)) {
    if (key === field) {
      return value as ConstraintFilterCondition
    }
  }

  return null
}

// Resolves a filter-list ID to its human-readable name for in_filter_list /
// not_in_filter_list conditions. Pass an identity resolver if no registry exists.
type GetFilterListName = (id: string) => string

export function describeFilter(
  filter: FilterObject | null | undefined,
  getFieldName: (field: string) => string,
  getFilterListName: GetFilterListName,
  getFormatter?: (field: string) => (value: ConditionValue) => string,
  depth = 0,
): string {
  return describeFilterTree(
    filter,
    {
      style: VERBOSE_STYLE,
      labelField: getFieldName,
      getValueFormatter: getFormatter,
      resolveListName: getFilterListName,
      maxDepth: MAX_FILTER_DEPTH,
      depthOverflowText: '',
    },
    depth,
  )
}

export function getFieldDisplayName(field: string): string {
  const refConfig =
    referenceDataTransformationConfig[
      field as keyof typeof referenceDataTransformationConfig
    ]
  if (refConfig) return refConfig.keyTransformation(field)

  globalLogger.warn('No referenceDataTransformationConfig found for field', {
    field,
  })
  return DEFAULT_TRANSFORMS.keyTransformation(field)
}

export const getFormattedFieldName = (
  field: string,
  instanceType?: ConstraintInstanceType,
): string => {
  if (
    instanceType === ConstraintInstanceType.Proposal &&
    PROPOSAL_FIELD_LABELS[field]
  ) {
    return PROPOSAL_FIELD_LABELS[field].toLowerCase()
  }

  return getFieldDisplayName(field).toLowerCase()
}

// Fields whose valueTransformation uses formatPrice (which hardcodes $).
// For these fields, use formatMoney with the correct currency instead.
const MONETARY_FIELDS = new Set([
  'amount_outstanding',
  'issue_size',
  'issue_minimum',
  'issue_minimum_denomination',
  'original_issue_size',
  'accrued_interest',
])

export function getFieldFormatter(
  field: string,
  currency: string = getCurrencyCode(),
): (value: ConditionValue) => string {
  if (MONETARY_FIELDS.has(field)) {
    return (value: ConditionValue) =>
      formatMoney(Number(value), { numDigits: 0, currency })
  }

  const config =
    referenceDataTransformationConfig[
      field as keyof typeof referenceDataTransformationConfig
    ]
  if (!config) {
    globalLogger.warn('No referenceDataTransformationConfig found for field', {
      field,
    })
  }
  const transform =
    config?.valueTransformation ?? DEFAULT_TRANSFORMS.valueTransformation
  return (value: ConditionValue) => transform(value as never)
}

export function renderConstraintText(params: {
  constraint: ModelConstraint | null | undefined
  displayMode: ConstraintDisplayMode
  displayScaleFactor?: number
  getFilterListName?: GetFilterListName
}): string {
  const { constraint, displayMode, displayScaleFactor, getFilterListName } =
    params
  // Aggregate validations (e.g. cashflow targets) are synthesized from
  // non-constraint inputs and carry no `constraint` object to render.
  if (!constraint) return ''
  switch (constraint.type) {
    case ModelConstraintType.Universe:
    case ModelConstraintType.BuyUniverse:
      return capitalize(renderUniverseConstraint(constraint, getFilterListName))
    case ModelConstraintType.Diversification:
      return capitalize(
        renderDiversificationConstraint(
          constraint,
          displayMode,
          displayScaleFactor,
        ),
      )
    case ModelConstraintType.AverageValue:
      return capitalize(renderAverageValueConstraint(constraint))
    case ModelConstraintType.Allocation:
      return capitalize(
        renderAllocationConstraint(
          constraint,
          displayMode,
          displayScaleFactor,
          getFilterListName,
        ),
      )
    case ModelConstraintType.LadderPolicy:
      return renderLadderPolicyConstraint(constraint)
    case ModelConstraintType.Piecewise:
      return capitalize(
        renderPiecewiseConstraint(
          constraint,
          displayMode,
          displayScaleFactor,
          getFilterListName,
        ),
      )
    case ModelConstraintType.CashflowBand:
      return `Level cashflows within ${renderCashflowBandRange(constraint)} of their average`
    default:
      return ''
  }
}

export const renderCashflowBandRange = (
  constraint: CashflowBandConstraint,
): string =>
  `±${formatPercentage(decimalToPercentage(constraint.max_deviation_ratio), {
    numDigits: 2,
    removeTrailingZeros: true,
  })}`

export const renderEqualWeightTolerance = (tolerance: number): string =>
  `±${formatPercentage(decimalToPercentage(tolerance), {
    numDigits: 2,
    removeTrailingZeros: true,
  })}`

const renderLadderPolicyConstraint = (
  constraint: LadderPolicyConstraint,
): string => {
  const periodLength = constraint.period_length
  if (!periodLength) return 'Maturity ladder'

  const startPeriod = constraint.start_period ?? 0
  const endPeriod = constraint.end_period ?? startPeriod
  const isYearBucket = periodLength % 12 === 0
  const start = startPeriod * periodLength
  const end = endPeriod * periodLength

  if (isYearBucket) {
    return `Maturity ladder, ${start / 12}-${end / 12} year buckets`
  }
  return `Maturity ladder, ${start}-${end} month buckets`
}

// "for accounts up to $250K" / "for accounts above $250K" clause for a piece's
// account-market-value condition, or '' when the piece is unconditioned.
export const renderPieceBound = (piece: Piece): string => {
  const currency = getCurrencyCode()
  const { upper, lower } = readAccountValueBound(piece.condition)
  if (upper !== undefined) {
    return `for accounts up to ${formatMoney(upper, { currency })}`
  }
  if (lower !== undefined) {
    return `for accounts above ${formatMoney(lower, { currency })}`
  }
  return ''
}

// Renders an account-size-tiered rule. Uniform diversification pieces get the
// compact form ("Weight per issuer at most 10% for accounts up to $250K, at
// most 5% above"); any other uniform child type renders each tier's full
// description with its bound ("Allocate 5%-10% to X for accounts up to $250K,
// allocate 10%-15% to X for accounts above $250K"). Piecewise shapes that
// aren't single-child-per-piece have no rendering and return ''.
const renderPiecewiseConstraint = (
  constraint: PiecewiseConstraint,
  displayMode: ConstraintDisplayMode,
  displayScaleFactor?: number,
  getFilterListName?: GetFilterListName,
): string => {
  // Only single-child pieces are renderable; anything else (multi-constraint
  // pieces from other authoring surfaces) is skipped rather than blocking the
  // rest of the rule from rendering.
  const tiers = (constraint.pieces ?? []).flatMap((piece) => {
    if (piece.constraints?.length !== 1) return []
    const child = piece.constraints[0]
    return child ? [{ piece, child }] : []
  })
  if (tiers.length === 0) return ''

  // Compact form: every child is a diversification cap on the same field.
  const first = tiers[0]?.child
  const allSameFieldDiversification =
    first?.type === ModelConstraintType.Diversification &&
    tiers.every(
      ({ child }) =>
        child.type === ModelConstraintType.Diversification &&
        child.field === first.field &&
        child.equal_weight_tolerance == null,
    )
  if (allSameFieldDiversification) {
    const parts: string[] = []
    let fieldLabel: string | undefined
    for (const { piece, child } of tiers) {
      if (child.type !== ModelConstraintType.Diversification) continue
      fieldLabel = fieldLabel ?? getFieldDisplayName(child.field)
      const range = renderDiversificationRange(
        child,
        displayMode,
        displayScaleFactor,
      )
      if (!range) continue
      const bound = renderPieceBound(piece)
      parts.push(bound ? `${range} ${bound}` : range)
    }
    if (!fieldLabel || parts.length === 0) return ''
    return `Weight per ${fieldLabel} ${parts.join(', ')}`
  }

  // General form: each tier's full child description plus its bound clause.
  const parts: string[] = []
  for (const { piece, child } of tiers) {
    const text = renderConstraintText({
      constraint: child,
      displayMode,
      displayScaleFactor,
      getFilterListName,
    })
    if (!text) continue
    const bound = renderPieceBound(piece)
    // Tier descriptions are lowercased so the joined sentence reads as one
    // rule; the caller capitalizes the whole string once.
    const lowered = text.charAt(0).toLowerCase() + text.slice(1)
    parts.push(bound ? `${lowered} ${bound}` : lowered)
  }
  if (parts.length === 0) return ''
  return parts.join(', ')
}

const identityFilterListName: GetFilterListName = (id) => id

// Human-readable description of a scope filter (e.g. `S&P outlook Negative`),
// using the standard field names and value formatters. Scopes never reference
// filter lists, so list ids pass through unresolved.
export function describeScopeFilter(
  filter: FilterObject | null | undefined,
): string {
  return describeFilter(
    filter,
    getFieldDisplayName,
    identityFilterListName,
    getFieldFormatter,
  )
}

const renderUniverseConstraint = (
  constraint: UniverseConstraint | BuyUniverseConstraint,
  getFilterListName?: GetFilterListName,
): string => {
  const filter = constraint.filter as FilterObject
  return describeFilter(
    filter,
    getFieldDisplayName,
    getFilterListName ?? identityFilterListName,
    getFieldFormatter,
  )
}

export const renderAverageValueRange = (
  constraint: AverageValueConstraint,
): string => {
  const formatValue = getFieldFormatter(constraint.field)

  const target = isNil(constraint.target_average_value)
    ? null
    : formatValue(constraint.target_average_value)
  const min = isNil(constraint.min_average_value)
    ? null
    : formatValue(constraint.min_average_value)
  const max = isNil(constraint.max_average_value)
    ? null
    : formatValue(constraint.max_average_value)

  if (min !== null && max !== null) return `between ${min} and ${max}`
  if (min !== null) return `at least ${min}`
  if (max !== null) return `at most ${max}`
  if (target !== null) return `target ${target}`
  return ''
}

const renderAverageValueConstraint = (
  constraint: AverageValueConstraint,
): string => {
  const fieldName = getFieldDisplayName(constraint.field)
  const range = renderAverageValueRange(constraint)
  return range
    ? `Average ${fieldName} ${range}`
    : `Average ${fieldName} constraint`
}

export function selectDiversificationBounds(
  constraint: DiversificationConstraint,
): DiversificationBounds {
  if (
    constraint.max_market_value_per_unique_value !== undefined ||
    constraint.min_market_value_per_unique_value !== undefined
  ) {
    return {
      kind: 'market_value',
      min: constraint.min_market_value_per_unique_value,
      max: constraint.max_market_value_per_unique_value,
    }
  }
  if (
    constraint.max_instruments_per_unique_value !== undefined ||
    constraint.min_instruments_per_unique_value !== undefined
  ) {
    return {
      kind: 'instruments',
      min: constraint.min_instruments_per_unique_value,
      max: constraint.max_instruments_per_unique_value,
    }
  }
  return {
    kind: 'weight',
    min: constraint.min_weight_per_unique_value,
    max: constraint.max_weight_per_unique_value,
  }
}

export const renderDiversificationRange = (
  constraint: DiversificationConstraint,
  displayMode: ConstraintDisplayMode,
  displayScaleFactor?: number,
): string => {
  const bounds = selectDiversificationBounds(constraint)
  const isInDollarMode = isDollarMode(displayMode, displayScaleFactor)
  const totalInvestment = isInDollarMode ? displayScaleFactor : undefined

  const currency = getCurrencyCode()
  const formatValue = (value?: number) => {
    if (value === undefined) return undefined
    if (bounds.kind === 'instruments') {
      return String(value)
    }
    if (bounds.kind === 'market_value') {
      return formatMoney(value, { currency })
    }
    if (isInDollarMode && totalInvestment !== undefined) {
      const dollarValue = value * totalInvestment
      return formatMoney(dollarValue, { currency })
    }
    return formatPercentage(decimalToPercentage(value), {
      numDigits: 2,
      removeTrailingZeros: true,
    })
  }

  const minValue = formatValue(bounds.min)
  const maxValue = formatValue(bounds.max)

  if (minValue && maxValue) return `between ${minValue} and ${maxValue}`
  if (minValue) return `at least ${minValue}`
  if (maxValue) return `at most ${maxValue}`
  return ''
}

const renderCountRange = (min?: number, max?: number): string => {
  if (!isNil(min) && !isNil(max)) return `between ${min} and ${max}`
  if (!isNil(min)) return `at least ${min}`
  if (!isNil(max)) return `at most ${max}`
  return ''
}

const renderDiversificationConstraint = (
  constraint: DiversificationConstraint,
  displayMode: ConstraintDisplayMode,
  displayScaleFactor?: number,
): string => {
  const { kind } = selectDiversificationBounds(constraint)
  const isInDollarMode = isDollarMode(displayMode, displayScaleFactor)
  const fieldLabel = getFieldDisplayName(constraint.field)
  if (constraint.equal_weight_tolerance != null) {
    return `Equal weight per ${fieldLabel} within ${renderEqualWeightTolerance(constraint.equal_weight_tolerance)}`
  }
  let prefix = 'Weight per'
  if (kind === 'instruments') {
    prefix = 'Instruments per'
  } else if (kind === 'market_value' || isInDollarMode) {
    prefix = 'Market value per'
  }

  const range = renderDiversificationRange(
    constraint,
    displayMode,
    displayScaleFactor,
  )
  if (range) return `${prefix} ${fieldLabel} ${range}`

  const uniqueRange = renderCountRange(
    constraint.min_unique_fields,
    constraint.max_unique_fields,
  )
  if (uniqueRange) return `Unique ${fieldLabel} count ${uniqueRange}`

  return `${prefix} ${fieldLabel}`
}

export function selectAllocationBounds(
  constraint: AllocationConstraint,
): AllocationBounds {
  if (
    constraint.min_market_value !== undefined ||
    constraint.max_market_value !== undefined ||
    constraint.target_market_value !== undefined
  ) {
    return {
      kind: 'market_value',
      min: constraint.min_market_value,
      max: constraint.max_market_value,
      target: constraint.target_market_value,
    }
  }
  return {
    kind: 'weight',
    min: constraint.min_weight,
    max: constraint.max_weight,
    target: constraint.target_weight,
  }
}

export const renderAllocationRange = (
  constraint: AllocationConstraint,
  displayMode: ConstraintDisplayMode,
  displayScaleFactor?: number,
) => {
  const bounds = selectAllocationBounds(constraint)
  const isMarketValue = bounds.kind === 'market_value'
  const isInDollarMode = isDollarMode(displayMode, displayScaleFactor)
  const totalInvestment = isInDollarMode ? displayScaleFactor : undefined

  const currency = getCurrencyCode()
  const formatValue = (val?: number) => {
    if (val === undefined) return undefined
    if (isMarketValue) {
      return formatMoney(val, { currency })
    }
    if (isInDollarMode && totalInvestment !== undefined) {
      const dollarValue = val * totalInvestment
      return formatMoney(dollarValue, { currency })
    }
    return formatPercentage(decimalToPercentage(val), { numDigits: 1 })
  }

  const min = formatValue(bounds.min)
  const max = formatValue(bounds.max)
  const target = formatValue(bounds.target)

  let rangeDescription = ''
  if (min && max) {
    rangeDescription = `${min}-${max}`
  } else if (target) {
    rangeDescription = `target ${target}`
  } else if (min) {
    rangeDescription = `min ${min}`
  } else if (max) {
    rangeDescription = `max ${max}`
  }
  return rangeDescription
}

const renderAllocationConstraint = (
  constraint: AllocationConstraint,
  displayMode: ConstraintDisplayMode,
  displayScaleFactor?: number,
  getFilterListName?: GetFilterListName,
): string => {
  const filter = constraint.filter as FilterDictionaryFilter | FilterObject

  const rangeDescription = renderAllocationRange(
    constraint,
    displayMode,
    displayScaleFactor,
  )

  const filterDescription = describeFilter(
    filter as FilterObject,
    getFieldDisplayName,
    getFilterListName ?? identityFilterListName,
    getFieldFormatter,
  )

  if (filterDescription && rangeDescription) {
    return `allocate ${rangeDescription} to ${filterDescription}`
  }
  return filterDescription || `${rangeDescription} allocation`
}
